import { useEffect, useState } from "react";
import { Link, useParams } from "wouter";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  BookOpen,
  Check,
  Clock,
  Hourglass,
  MapPin,
  Share2,
  Users,
  Vote,
} from "lucide-react";
import type { ClubAPI } from "@shared/types/api";
import { useClub, useJoinClub } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useToast } from "@/components/ux/Toast";
import { useConfirm } from "@/components/ConfirmModal";
import { Loader } from "@/components/Loader";
import Markdown from "@/components/Markdown";
import { PollCard } from "../../components/PollCard";
import { BoardCard, ComingSoonBadge, GateNotice, MeetupPlannedChip } from "./clubComponents";
import { useClubGate } from "./useClubGate";
import { OutcloseAvatar } from "./ClubAuthorAvatar";
import { ClubForumPreview } from "./ClubForumPreview";

function eventDate(e: ClubAPI.ClubEvent) {
  return new Date(`${e.date}T${e.time}`);
}

function formatEventDate(e: ClubAPI.ClubEvent) {
  return eventDate(e).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/** "3d 4h", "5h 12m", "8m" — compact countdown for the header chips. */
function formatCountdown(ms: number) {
  const mins = Math.max(0, Math.floor(ms / 60000));
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Re-renders every minute so countdowns stay current. */
function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function SectionHeading({ title, subtitle, count }: { title: string; subtitle?: string; count?: number }) {
  return (
    <div className="mb-3">
      <h2 className="text-lg font-bold">
        {title}
        {count != null && <span className="ml-1.5 text-sm font-normal text-dark-muted">{count}</span>}
      </h2>
      {subtitle && <p className="text-sm text-dark-muted">{subtitle}</p>}
    </div>
  );
}

function Chip({ icon: Icon, children, accent }: { icon: typeof Clock; children: React.ReactNode; accent?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
        accent ? "bg-primary-500/15 text-primary-500" : "bg-neutral-200/70 dark:bg-neutral-800 text-dark-muted"
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {children}
    </span>
  );
}

export default function ClubDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useT();
  const toast = useToast();
  const { confirm } = useConfirm();
  const { data: club, isLoading, error } = useClub(slug);
  const join = useJoinClub(slug);
  const gate = useClubGate();
  const now = useNow();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  if (error || !club) {
    return (
      <div className="pt-4 pb-24 md:pt-8 px-4 max-w-3xl mx-auto">
        <Link href="/clubs" className="btn-ghost mb-6 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> {t("clubs.back")}
        </Link>
        <div className="card text-center py-12">
          <BookOpen className="w-12 h-12 text-dark-muted mx-auto mb-3" />
          <p className="text-dark-muted">{t("clubs.notFound")}</p>
        </div>
      </div>
    );
  }

  const { access } = club;
  // An unopened club is a teaser: joining registers interest, nothing inside is unlocked yet.
  const unopened = club.status === "unopened";

  const joinClub = () =>
    join.mutate(true, {
      onSuccess: () => toast.success(t(unopened ? "clubs.interestedToast" : "clubs.joinedToast")),
      onError: (e) => toast.error(e.message),
    });

  const leaveClub = async () => {
    const ok = await confirm({
      message: t(unopened ? "clubs.withdrawInterestConfirm" : "clubs.leaveConfirm"),
      confirmText: t(unopened ? "clubs.withdrawInterest" : "clubs.leave"),
    });
    if (!ok) return;
    join.mutate(false, {
      onSuccess: () => toast.success(t("clubs.leftToast")),
      onError: (e) => toast.error(e.message),
    });
  };

  const share = async () => {
    const url = window.location.href.split("?")[0];
    try {
      if (navigator.share) {
        await navigator.share({ title: club.name, text: club.tagline ?? undefined, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success(t("clubs.linkCopied"));
    } catch {
      // Share sheet dismissed — nothing to do.
    }
  };

  // Header chips: soonest closing poll and the next meetup.
  const closingPoll = club.polls
    .filter((p) => p.endsAt && new Date(p.endsAt).getTime() > now)
    .sort((a, b) => new Date(a.endsAt!).getTime() - new Date(b.endsAt!).getTime())[0];
  const nextEvent = club.upcomingEvents.find((e) => eventDate(e).getTime() > now) ?? null;

  const openPoll = club.polls.find((p) => !p.hasResponded);
  const allVoted = club.polls.length > 0 && !openPoll;
  const cityName = club.city?.cityName;

  return (
    <div className="pt-4 pb-24 md:pt-8 px-4">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="space-y-5">
          <Link href="/clubs" className="btn-ghost inline-flex items-center gap-2 -ml-2">
            <ArrowLeft className="w-4 h-4" /> {t("clubs.back")}
          </Link>

          {/* Cover */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl overflow-hidden aspect-square w-full sm:max-w-md sm:mx-auto bg-primary-500/15">
            {club.imageUrl ? (
              <img src={club.imageUrl} alt="" className="w-full h-full object-contain bg-neutral-100 dark:bg-neutral-800" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookOpen className="w-14 h-14 text-primary-500" />
              </div>
            )}
          </motion.div>

          {/* Title + meta */}
          <div className="space-y-2">
            {unopened && <ComingSoonBadge />}
            <h1 className="text-2xl font-bold leading-tight">{club.name}</h1>
            {club.tagline && <p className="text-dark-muted">{club.tagline}</p>}
            <ul className="space-y-1 text-sm text-dark-muted">
              {cityName && (
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {cityName}
                </li>
              )}
              <li className="flex items-center gap-2">
                <Users className="w-4 h-4" />{" "}
                {t(unopened ? "clubs.interested" : "clubs.members", { count: club.memberCount })}
              </li>
              <li className="flex items-center gap-2">
                <OutcloseAvatar size="xs" /> {t("clubs.runBy")}
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button onClick={share} className="btn btn-outline rounded-full gap-2">
              <Share2 className="w-4 h-4" /> {t("clubs.share")}
            </button>
            {club.isMember ? (
              <button onClick={leaveClub} disabled={join.isPending} className="btn btn-primary rounded-full flex-1 gap-2">
                <Check className="w-4 h-4" /> {t(unopened ? "clubs.youreInterested" : "clubs.youreAMember")}
              </button>
            ) : (
              <button onClick={joinClub} disabled={join.isPending} className="btn btn-primary rounded-full flex-1">
                {t(unopened ? "clubs.joinWaitlist" : "clubs.join")}
              </button>
            )}
          </div>

          {/* Countdown chips (and "meetup in the works" before one is scheduled) */}
          {(closingPoll || nextEvent || club.city?.meetupReady) && (
            <div className="flex flex-wrap gap-2">
              {closingPoll && (
                <Chip icon={Vote} accent>
                  {t("clubs.votesCloseIn", { time: formatCountdown(new Date(closingPoll.endsAt!).getTime() - now) })}
                </Chip>
              )}
              {nextEvent ? (
                <Chip icon={Clock}>
                  {t("clubs.nextMeetingIn", { time: formatCountdown(eventDate(nextEvent).getTime() - now) })}
                </Chip>
              ) : (
                <MeetupPlannedChip city={club.city} />
              )}
            </div>
          )}
        </div>

        {/* Vote */}
        {club.polls.length > 0 && (
          <section>
            <SectionHeading
              title={allVoted ? t("clubs.voteDone") : t("clubs.voteOpen")}
              subtitle={t("clubs.voteSubtitle")}
            />
            <div className="space-y-3">
              {club.polls.map((poll) =>
                access.can.votePolls ? (
                  <PollCard key={poll.id} poll={poll} />
                ) : (
                  <div key={poll.id} className="card space-y-3">
                    <Markdown size="lg" className="font-bold">{poll.question}</Markdown>
                    <GateNotice message={t("clubs.subscribeToVote")} actionLabel={t("clubs.subscribe")} onAction={gate.openSubscribe} />
                  </div>
                ),
              )}
            </div>
          </section>
        )}

        {/* Board — pinned threads, above the rest of the forum */}
        {club.pinnedPosts.length > 0 && (
          <section>
            <SectionHeading title={t("clubs.board")} subtitle={t("clubs.boardSubtitle")} />
            <div className="space-y-3">
              {club.pinnedPosts.map((post) => (
                <BoardCard key={post.id} post={post} slug={club.slug} />
              ))}
            </div>
          </section>
        )}

        {/* Forum — latest discussions (an unopened club explains what's coming instead) */}
        {unopened ? (
          <section className="card flex items-start gap-3">
            <span className="w-11 h-11 rounded-xl bg-primary-500/15 text-primary-500 flex items-center justify-center flex-shrink-0">
              <Hourglass className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h2 className="text-lg font-bold leading-tight">
                {t(club.isMember ? "clubs.unopenedJoinedTitle" : "clubs.unopenedTitle")}
              </h2>
              <p className="text-sm text-dark-muted">
                {t(club.isMember ? "clubs.unopenedJoinedDescription" : "clubs.unopenedDescription")}
              </p>
            </div>
          </section>
        ) : (
          <ClubForumPreview club={club} onJoin={joinClub} onSubscribe={gate.openSubscribe} />
        )}

        {/* About */}
        {club.description && (
          <section>
            <SectionHeading title={t("clubs.about")} />
            <Markdown className="text-dark-muted">{club.description}</Markdown>
          </section>
        )}

        {/* Upcoming meetups */}
        {club.upcomingEvents.length > 0 && (
          <section>
            <SectionHeading title={t("clubs.upcomingEvents")} count={club.upcomingEvents.length} />
            <div className="space-y-3">
              {club.upcomingEvents.map((event) => {
                const body = (
                  <>
                    {(event.coverImageUrl || club.imageUrl) && (
                      <div className="bg-neutral-100 dark:bg-neutral-800">
                        <img src={event.coverImageUrl ?? club.imageUrl!} alt="" className="w-full sm:max-w-sm sm:mx-auto aspect-square object-contain" />
                      </div>
                    )}
                    <div className="p-4 space-y-1">
                      <div className="font-bold">{event.name}</div>
                      <div className="text-sm text-dark-muted">{formatEventDate(event)}</div>
                    </div>
                  </>
                );
                return access.can.attendEvent && event.slug ? (
                  <Link key={event.id} href={`/events/${event.slug}`} className="card block p-0 overflow-hidden hover:ring-2 hover:ring-primary-500/40">
                    {body}
                  </Link>
                ) : (
                  <div key={event.id} className="card p-0 overflow-hidden">
                    {body}
                    <div className="px-4 pb-4">
                      {access.isMember ? (
                        <GateNotice message={t("clubs.subscribeToAttend")} actionLabel={t("clubs.subscribe")} onAction={gate.openSubscribe} />
                      ) : (
                        <GateNotice message={t("clubs.joinToParticipate")} actionLabel={t("clubs.join")} onAction={joinClub} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
      {gate.interstitial}
    </div>
  );
}
